<template>
  <v-dialog
    v-model="showDialog"
    :disabled="!isEditAllowed"
    persistent
    class="custom-scrollbar"
    max-width="1100px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        :disabled="!isEditAllowed"
        :loading="loading"
        icon
        dark
        v-bind="attrs"
        v-on="on"
      >
        <v-icon title="Projekt bearbeiten"> mdi-file-edit </v-icon>
      </v-btn>
    </template>
    <v-card :loading="loading">
      <v-toolbar color="secondary" dark>
        <span class="bold">
          Projekt {{ project.projektId }} - {{ project.name }} bearbeiten
        </span>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <template v-if="!loading">
          <v-container>
            <v-row>
              <v-col cols="2"></v-col>
              <v-col cols="5">Neuer Wert</v-col>
              <v-col cols="5">Wert nach Datum</v-col>
            </v-row>
            <v-row>
              <v-col cols="2"></v-col>
              <v-col cols="5">
                <v-checkbox
                  v-model="timedRollback"
                  label="Temporäre Änderung"
                  dense
                ></v-checkbox>
              </v-col>
              <v-col cols="5">
                {{ dueDate }}
                <v-icon
                  :disabled="!timedRollback"
                  class="mt-0 mr-4"
                  title=""
                  style="cursor: pointer"
                  @click.stop="modifyDueDateDialog = true"
                >
                  {{ icons.mdiTimerEditOutline }}
                </v-icon>
              </v-col>
            </v-row>
            <v-row v-if="isDistributionEditAllowed">
              <v-col cols="2">Vertriebsphase</v-col>
              <v-col cols="5">
                <v-autocomplete
                  v-model="updateDistributionStatus"
                  :items="newDistributionStatusItems"
                  dense
                  item-text="description"
                  item-value="name"
                >
                  <template v-slot:selection="{ item }">
                    <span :style="{ color: item.color }">{{
                      item.description
                    }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span :style="{ color: item.color }">{{
                      item.description
                    }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="5">
                <v-autocomplete
                  v-model="timedDistributionStatus"
                  :items="timedDistributionStatusItems"
                  :disabled="!isTimedEnabled || !timedRollback"
                  dense
                  item-text="description"
                  item-value="name"
                >
                  <template v-slot:selection="{ item }">
                    <span :style="{ color: item.color }">{{
                      item.description
                    }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span :style="{ color: item.color }">{{
                      item.description
                    }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row v-if="isTariffModelEditAllowed">
              <v-col cols="2">Tarifmodell</v-col>
              <v-col cols="5">
                <v-autocomplete
                  v-model="updateTariffModel"
                  :items="newTariffModelItems"
                  dense
                  item-text="name"
                  item-value="tariffId"
                >
                  <template v-slot:item="{ item }">
                    <span>{{
                      item.tariffId === NO_MODIFY ? '-' : item.tariffId
                    }}</span>
                    <span style="position: absolute; left: 75px">{{
                      item.name
                    }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="5">
                <v-autocomplete
                  v-model="timedTariffModel"
                  :items="timedTariffModelItems"
                  :disabled="!isTimedEnabled || !timedRollback"
                  dense
                  item-text="name"
                  item-value="tariffId"
                >
                  <template v-slot:item="{ item }">
                    <span>{{
                      item.tariffId === NO_MODIFY ? '-' : item.tariffId
                    }}</span>
                    <span style="position: absolute; left: 75px">{{
                      item.name
                    }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </template>
        <template v-else>
          <span>Daten werden geladen...</span>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-btn color="secondary" @click="showDialog = false"> Abbrechen </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!loading"
          :disabled="!showDialog || !hasAnyInputChanged"
          color="primary"
          @click="reviewChangesModal = true"
        >
          Prozess starten
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="modifyDueDateDialog" width="700px">
      <v-card>
        <v-card-text class="pt-6">
          <v-container>
            <v-row>
              <v-col cols="6">
                <v-date-picker
                  v-model="dueDateDate"
                  class="mt-4"
                  :min="new Date().toISOString().split('T')[0]"
                ></v-date-picker>
              </v-col>
              <v-col cols="6">
                <v-time-picker v-model="dueDateTime" format="24hr" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="modifyDueDateDialog = !modifyDueDateDialog"
          >
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="reviewChangesModal"
      :persistent="submitting"
      :width="dueDate !== null ? '850px' : '700px'"
    >
      <v-card>
        <v-card-text class="pt-6">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left"></th>
                        <th class="text-left">Alter Wert</th>
                        <th class="text-left">Neuer Wert</th>
                        <th v-if="dueDate !== null" class="text-left">
                          Wert am {{ dueDate }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="hasAnyDistributionStatusChanged">
                        <td>Vertriebsphase</td>
                        <td>
                          {{ projectDistributionStatus.description }}
                        </td>
                        <td>
                          <template v-if="hasDistributionStatusChanged">
                            {{
                              getDistributionStatusByName(
                                updateDistributionStatus
                              ).description
                            }}
                          </template>
                        </td>
                        <td v-if="dueDate !== null">
                          <template v-if="hasTimedDistributionStatusChanged">
                            {{
                              getDistributionStatusByName(
                                timedDistributionStatus
                              ).description
                            }}
                          </template>
                        </td>
                      </tr>
                      <tr v-if="hasAnyTariffModelChanged">
                        <td>Tarifmodell</td>
                        <td>
                          {{ projectTariffModel.name }}
                        </td>
                        <td>
                          <template v-if="hasTariffModelChanged">
                            {{
                              getTariffModelByTariffId(updateTariffModel).name
                            }}
                          </template>
                        </td>
                        <td v-if="dueDate !== null">
                          <template v-if="hasTimedTariffModelChanged">
                            {{
                              getTariffModelByTariffId(timedTariffModel).name
                            }}
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="secondary"
            @click="reviewChangesModal = !reviewChangesModal"
          >
            Abbrechen
          </v-btn>
          <v-spacer />
          <v-btn color="primary" :loading="submitting" @click="submit()">
            Ja, Prozess starten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import NotificationObject from '@/main/NotificationObject.js';
import { HTTP } from '@/main/httpClient.js';
import { adminGroups } from '@/main/routes';

import { mdiTimerEditOutline } from '@mdi/js';

const TIMEZONE = 'Europe/Berlin';
const NO_MODIFY = 'no-modify';

export default {
  name: 'StartProjectChange',
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    NO_MODIFY,
    icons: {
      mdiTimerEditOutline
    },
    loading: false,
    submitting: false,
    showDialog: false,
    modifyDueDateDialog: false,
    reviewChangesModal: false,
    tariffModels: [],
    timedRollback: false,
    updateTariffModel: null,
    timedTariffModel: null,
    distributionStatus: [],
    updateDistributionStatus: null,
    timedDistributionStatus: null,
    dueDateDate: null,
    dueDateTime: null
  }),
  computed: {
    userGroups: function () {
      return this.$auth.user().userRoles;
    },
    isAdmin: function () {
      return this.userHasAnyGroup(adminGroups);
    },
    isDistributionEditAllowed: function () {
      return this.isAdmin || this.userHasAnyGroup('LgSpotEcommerce');
    },
    isTariffModelEditAllowed: function () {
      return this.isAdmin || this.userHasAnyGroup('LgSpotEcommerce');
    },
    isEditAllowed: function () {
      return (
        this.isAdmin ||
        this.isDistributionEditAllowed ||
        this.isTariffModelEditAllowed
      );
    },
    isTimedEnabled: function () {
      return this.dueDate !== null;
    },
    projectDistributionStatus: function () {
      return this.distributionStatus.find(
        (d) => this.project.distributionStatus.id === d.id
      );
    },
    projectTariffModel: function () {
      return this.tariffModels.find(
        (t) => this.project.tariffModel.id === t.id
      );
    },
    dueDate() {
      if (!this.dueDateDate || !this.dueDateTime) return null;

      const newDueDate = new Date(this.dueDateDate + ' ' + this.dueDateTime);

      return newDueDate.toLocaleString('de-DE', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    hasDistributionStatusChanged() {
      return (
        this.projectDistributionStatus.name !== this.updateDistributionStatus &&
        this.updateDistributionStatus !== NO_MODIFY
      );
    },
    hasTimedDistributionStatusChanged() {
      return (
        this.timedRollback &&
        this.dueDate !== null &&
        this.timedDistributionStatus !== this.updateDistributionStatus &&
        this.timedDistributionStatus !== NO_MODIFY
      );
    },
    hasAnyDistributionStatusChanged() {
      return (
        this.hasDistributionStatusChanged ||
        this.hasTimedDistributionStatusChanged
      );
    },
    hasTariffModelChanged() {
      return (
        this.projectTariffModel.tariffId !== this.updateTariffModel &&
        this.updateTariffModel !== NO_MODIFY
      );
    },
    hasTimedTariffModelChanged() {
      return (
        this.timedRollback &&
        this.dueDate !== null &&
        this.timedTariffModel !== this.updateTariffModel &&
        this.timedTariffModel !== NO_MODIFY
      );
    },
    hasAnyTariffModelChanged() {
      return this.hasTariffModelChanged || this.hasTimedTariffModelChanged;
    },
    hasAnyInputChanged() {
      return (
        this.hasAnyDistributionStatusChanged || this.hasAnyTariffModelChanged
      );
    },
    newTariffModelItems() {
      return this.tariffModels.map(t => ({
        ...t,
        disabled: t.id === this.projectTariffModel.id
      }));
    },
    newDistributionStatusItems() {
      return this.distributionStatus.map(d => ({
        ...d,
        disabled: d.id === this.projectDistributionStatus.id
      }));
    },
    timedTariffModelItems() {
      return this.tariffModels.map(t => ({
        ...t,
        disabled: t.tariffId === this.updateTariffModel
      }));
    },
    timedDistributionStatusItems() {
      return this.distributionStatus.map(d => ({
        ...d,
        disabled: d.name === this.updateDistributionStatus
      }));
    }
  },
  watch: {
    showDialog: async function (newVal) {
      if (!newVal) return;
      this.loading = true;

      await Promise.all([
        this.loadTariffModels(),
        this.loadDistributionStatus()
      ]);

      this.tariffModels = [
        {
          name: 'Nicht ändern',
          tariffId: NO_MODIFY
        },
        ...this.tariffModels
      ];

      this.distributionStatus = [
        {
          color: null,
          description: 'Nicht ändern',
          name: NO_MODIFY
        },
        ...this.distributionStatus
      ];

      this.updateDistributionStatus = NO_MODIFY;
      this.timedDistributionStatus = NO_MODIFY;
      this.updateTariffModel = NO_MODIFY;
      this.timedTariffModel = NO_MODIFY;

      this.loading = false;
    },
    updateDistributionStatus: function (newVal) {
      if (this.timedDistributionStatus === newVal) {
        this.timedDistributionStatus = NO_MODIFY;
      }
    },
    updateTariffModel: function (newVal) {
      if (this.timedTariffModel === newVal) {
        this.timedTariffModel = NO_MODIFY;
      }
    }
  },
  methods: {
    submit: function () {
      if (this.submitting || this.loading) return;
      if (!this.isEditAllowed) return this.notAllowed();

      this.submitting = true;
      const startDto = {};

      if (this.timedRollback && this.dueDate !== null) {
        startDto.restoreDate = this.toJavaDate(
          new Date(this.dueDateDate + ' ' + this.dueDateTime)
        );
      }

      // Distribution status
      if (this.isDistributionEditAllowed) {
        if (this.hasDistributionStatusChanged) {
          startDto.newDistributionStatusName = this.updateDistributionStatus;
        }
        if (this.hasTimedDistributionStatusChanged) {
          startDto.restoreDistributionStatusName = this.timedDistributionStatus;
        }
      } else return this.notAllowed();

      // Tariff Model
      if (this.isTariffModelEditAllowed) {
        if (this.hasTariffModelChanged) {
          startDto.newTariffModelName = this.getTariffModelByTariffId(
            this.updateTariffModel
          ).name;
        }
        if (this.hasTimedTariffModelChanged) {
          startDto.restoreTariffModelName = this.getTariffModelByTariffId(
            this.timedTariffModel
          ).name;
        }
      } else return this.notAllowed();

      HTTP.post(
        `projectConstructionState/project-update/${this.project.projektId}`,
        startDto
      )
        .then(() => {
          this.showToast('success', 'Der Prozess wurde gestartet');
          this.reviewChangesModal = false;
          this.resetInputs();
          this.submitting = false;
          this.showDialog = false;
        })
        .catch(() => {
          this.reviewChangesModal = false;
          this.submitting = false;
          this.showToast('error', 'Der Prozess konnte nicht gestartet werden!');
        });
    },
    userHasAnyGroup(groupsToCheck) {
      if (!Array.isArray(groupsToCheck)) {
        groupsToCheck = [groupsToCheck];
      }

      return groupsToCheck.some((g) => this.userGroups.includes(g));
    },
    loadTariffModels: function () {
      if (this.tariffModels.length > 0) return;

      return HTTP.get('past/tariffModels').then((res) => {
        this.tariffModels = res.data;
      });
    },
    loadDistributionStatus: function () {
      if (this.distributionStatus.length > 0) return;

      return HTTP.get('past/distributionStatuses').then((res) => {
        this.distributionStatus = res.data;
      });
    },
    getDistributionStatusByName(name) {
      return this.distributionStatus.find((d) => d.name === name);
    },
    getTariffModelByTariffId(id) {
      return this.tariffModels.find((t) => t.tariffId === id);
    },
    toJavaDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      const offset = -date.getTimezoneOffset();
      const sign = offset >= 0 ? '+' : '-';
      const absOffset = Math.abs(offset);

      const offsetStr =
        `${sign}${String(Math.floor(absOffset / 60)).padStart(2, '0')}:` +
        `${String(absOffset % 60).padStart(2, '0')}`;

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetStr}`;
    },
    showToast: function (type, message) {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, message)
      );
    },
    notAllowed: function () {
      this.showToast(
        'error',
        'Es fehlen die korrekten Berechtigungen um diese Aktion auszuführen'
      );
    },
    resetInputs: function () {
      this.dueDateDate = null;
      this.dueDateTime = null;
      this.updateDistributionStatus = NO_MODIFY;
      this.updateTariffModel = NO_MODIFY;
      this.timedDistributionStatus = NO_MODIFY;
      this.timedTariffModel = NO_MODIFY;
    }
  }
};
</script>
